<template>
  <stepper-layout :stepTitle="'Rental Options'" :stepIcon="'shopping_cart'">
    <v-expansion-panels
      data-testid="rental-options-expansion-panel"
      class="rental-options-expansion-panel"
      v-model="panel"
      focusable
    >
      <v-expansion-panel
        data-testid="rental-options-tenant-expansion-panel"
        class="rental-options-tenant-expansion-panel"
        style="outline: grey 1px solid"
      >
        <v-expansion-panel-header>
          Enter Tenant Information
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container>
            <v-row class="mt-4">
              <facility-tenant-search @tenantSelected="setTenant($event)" />
              <v-btn
                data-testid="rental-reset-form-btn"
                class="rental-reset-form-btn ml-4"
                @click="handleResetForm"
                outlined
                color="red"
              >
                Reset Form
              </v-btn>
              <v-checkbox
                data-testid="rental-is-business-customer-checkbox"
                class="rental-is-business-customer-checkbox ml-4 mt-0 pt-0"
                v-model="isBusinessCustomer"
              >
                <template v-slot:label>
                  <div class="subheading">Business Customer</div>
                </template>
              </v-checkbox>
            </v-row>
          </v-container>
          <v-form ref="form">
            <v-row no-gutters>
              <v-col>
                <app-input
                  data-testid="rent-first-name-input"
                  class="rent-first-name-input mr-2"
                  label="First Name"
                  v-model="firstName"
                  :rules="'required'"
                  filled
                ></app-input>
              </v-col>
              <v-col>
                <app-input
                  data-testid="rent-last-name-input"
                  class="rent-last-name-input ml-2"
                  label="Last Name"
                  v-model="lastName"
                  :rules="'required'"
                  filled
                ></app-input>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <app-input
                  data-testid="rent-billing-address-input"
                  class="rent-billing-address-input mr-2"
                  label="Billing Address"
                  v-model="address"
                  :rules="'required'"
                  filled
                ></app-input>
              </v-col>
              <v-col>
                <app-input
                  data-testid="rent-billing-city-input"
                  class="rent-billing-city-input ml-2"
                  label="City"
                  v-model="city"
                  :rules="'required'"
                  filled
                ></app-input>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <v-select
                  data-testid="rent-states-menu"
                  class="rent-states-menu mr-2"
                  label="State"
                  v-model="state"
                  :rules="[(v) => !!v || 'This field is required']"
                  :items="states"
                  item-text="value"
                />
              </v-col>
              <v-col>
                <app-input
                  data-testid="rent-postal-code-input"
                  class="rent-postal-code-input ml-2"
                  label="Postal Code"
                  v-model="postalCode"
                  :rules="'required|digits:5'"
                  filled
                  hint="5 digits only"
                  :maxLength="5"
                ></app-input>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <app-input
                  data-testid="rent-phone-number-input"
                  class="rent-phone-number-input mr-2"
                  label="Phone Number"
                  v-model="phone"
                  type="phone"
                  :rules="'required|phone'"
                  filled
                ></app-input>
              </v-col>
              <v-col>
                <app-input
                  data-testid="rent-email-input"
                  class="rent-email-input ml-2"
                  label="Email Address"
                  v-model="email"
                  :rules="'required|email'"
                  filled
                ></app-input>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <app-input
                  v-if="isBusinessCustomer"
                  data-testid="rent-company-name-input"
                  class="rent-company-name-input ml-2"
                  label="Company Name"
                  v-model="companyName"
                  :rules="'required'"
                  filled
                ></app-input>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <v-select
                  data-testid="rent-id-types-menu"
                  class="rent-id-types-menu mr-2"
                  label="ID Type"
                  :rules="[(v) => !!v || 'This field is required']"
                  return-object
                  @change="updateIDType()"
                  :items="idTypes"
                  item-text="name"
                  v-model="idTypeOption"
                />
              </v-col>
              <v-col>
                <app-input
                  :disabled="selectedOption.name ? false : true"
                  data-testid="rent-id-number-input"
                  class="rent-id-number-input mr-2"
                  :label="selectedOption.name"
                  v-model="idNumber"
                  :placeholder="`Your ${selectedOption.name ? `${selectedOption.name}` : 'ID Number'}...`"
                  :rules="'required'"
                  filled
                ></app-input>
              </v-col>
            </v-row>
            <v-row no-gutters v-if='["1", "2"].includes(selectedOption.id)'>
              <v-col>
                <v-select
                  data-testid="rent-id-state-menu"
                  class="rent-id-state-menu mr-2"
                  :label="`${selectedOption.name ? `State (${selectedOption.name})` : 'Your ID State'}`"
                  :rules="[(v) => !!v || 'This field is required']"
                  return-object
                  :items="states"
                  item-text="text"
                  v-model="idStateOption"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel
        data-testid="rental-options-facility-expansion-panel"
        class="rental-options-facility-expansion-panel"
        style="outline: grey 1px solid; max-height: 25rem; overflow: auto"
      >
        <v-expansion-panel-header>
          Select Facility Unit
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="mt-4 mb-2">
            <div class="subheading font-weight-bold">
              Current selection:
              <span v-if="unit"> Unit {{ unit.name }} </span>
            </div>
            <v-btn
              data-testid="rent-show-units-btn"
              class="rent-show-units-btn mx-0 mt-4 primary"
              outlined
              v-if="!showAvailableUnits"
              @click="showAvailableUnits = true"
            >
              <v-icon class="mr-2">arrow_drop_down</v-icon>
              Show All Available {{ unitDimensions }} Units
            </v-btn>
            <v-btn
              data-testid="rent-hide-units-btn"
              class="rent-hide-units-btn mx-0 mt-4 primary"
              outlined
              v-if="showAvailableUnits"
              @click="showAvailableUnits = false"
            >
              <v-icon class="mr-2">arrow_drop_up</v-icon>
              Hide Unit List
            </v-btn>
          </div>
          <div v-if="showAvailableUnits">
            <div
              class="mx-4"
              v-for="(availableUnit, i) in availableUnits"
              :key="i"
            >
              <v-layout justify-space-between align-center>
                <div class="subheading font-weight-regular">
                  Unit {{ availableUnit.name }}
                  / ${{ availableUnit.rate }}
                </div>
                <v-btn
                  data-testid="rent-select-unit-btn"
                  class="rent-select-unit-btn my-2"
                  :outlined="availableUnit.name === unit.name"
                  color="primary"
                  style="width: 10rem"
                  @click="selectUnit(availableUnit)"
                >
                  <span
                    v-if="availableUnit.name === unit.name"
                    class="primary--text"
                  >
                    Selected
                    <v-icon style="font-size: 1.5rem" class="ml-1"
                      >check</v-icon
                    >
                  </span>
                  <span v-else>Select Unit</span>
                </v-btn>
              </v-layout>
              <v-divider v-if="i < availableUnits.length - 1" />
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel
        data-testid="rental-options-move-in-promo-expansion-panel"
        class="rental-options-move-in-promo-expansion-panel"
        style="outline: grey 1px solid"
      >
        <v-expansion-panel-header>
          <div class="ma-0 pa-0">
            <v-icon v-if="invalidPromotion" class="my-0 py-0" left color="red" dense>
              warning
            </v-icon>
            Select Move-in Date + Promotion
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-layout v-if="moveInCostLoading" justify-center class="mt-4">
            <v-progress-circular
              :size="70"
              :width="7"
              indeterminate
              class="mb-3"
            />
          </v-layout>
          <v-layout v-else class="mt-4">
            <v-flex>
              <v-menu
                data-testid="rent-move-in-menu"
                class="rent-move-in-menu"
                ref="moveIn"
                v-model="moveInSelect"
                :close-on-content-click="false"
                :return-value.sync="moveInDate"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    data-testid="rent-move-in-date-input"
                    class="rent-move-in-date-input pr-2"
                    v-model="moveInDate"
                    label="Move-in Date"
                    prepend-icon="event"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  data-testid="rent-move-in-date-picker"
                  class="rent-move-in-date-picker"
                  v-model="moveInDate"
                  no-title
                  scrollable
                  :min="today"
                  :max="moveInMax"
                  @change="moveInSelect = false"
                >
                </v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex class="ml-4">
              <v-expand-transition>
                <div v-if="invalidPromotion">
                  <v-progress-circular
                    v-if="facilityPromotionsLoading"
                    :size="70"
                    :width="7"
                    indeterminate
                    class="mb-3"
                  />
                  <div v-else-if="userMustSelectANewPromotion">
                    These promotions are available for this facility:
                    <v-radio-group v-model="defaultPromotionOptionId">
                      <v-radio
                        label="Disable promotion for this reservation"
                        :value="0"
                      />
                      <v-radio
                        v-for="(promotion, key) in facilityPromotions"
                        :key="key"
                        :label="promotion.text"
                        :value="promotion.external_id"
                        :return-object="true"
                        />
                    </v-radio-group>
                  </div>
                </div>
              </v-expand-transition>
              <v-expand-transition>
                <div v-if="promotionOptionsLoading">
                  Promotion options loading...
                </div>
                <div v-else-if="defaultPromotionOption">
                  <div class="text-body-2">
                    Promotion Offered:
                    {{ defaultPromotionOption.text }}
                  </div>
                  <div class="text-caption mt-2">Apply promotion...</div>
                  <v-radio-group row v-model="selectedPromotion" class="my-2">
                    <v-radio
                      data-testid="this-month-promotion-radio"
                      class="this-month-promotion-radio"
                      v-if="promotionOptions.this_month"
                      label="This Month"
                      :value="promotionOptions.this_month.id"
                      @change="deferPromotion(false)"
                    />
                    <v-radio
                      data-testid="next-month-promotion-radio"
                      class="next-month-promotion-radio"
                      v-if="promotionOptions.next_month"
                      label="Next Month"
                      :value="promotionOptions.next_month.id"
                      @change="deferPromotion(true)"
                    />
                    <v-radio
                      data-testid="promotion-dont-apply-radio"
                      class="promotion-dont-apply-radio"
                      off-icon="close"
                      label="Do Not Apply"
                      v-if="!invalidPromotion"
                      :value="0"
                      @change="deferPromotion(false)"
                    />
                  </v-radio-group>
                </div>
                <div class="text-h6 ma-3" v-else-if="!invalidPromotion">
                  No promotion available for this unit.
                </div>
                <div v-else-if="promotionOptions && !userMustSelectANewPromotion">
                  <div class="text-body-2">
                    Promotion Offered: None
                  </div>
                </div>
              </v-expand-transition>
              <v-expand-transition>
                <v-btn
                  data-testid="promotion-choose-another-radio"
                  class="promotion-choose-another-radio pt-0 mt-0"
                  v-if="userCanChooseAnotherPromotion"
                  @click="promotionOptions = null; selectedPromotion = null"
                  text
                >
                Choose another promotion
              </v-btn>
              </v-expand-transition>
            </v-flex>
          </v-layout>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel
        data-testid="rental-options-tpp-expansion-panel"
        class="rental-options-tpp-expansion-panel"
        style="outline: grey 1px solid"
      >
        <v-expansion-panel-header>
          Select Tenant Protection Plan
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-radio-group v-model="tenantProtection" class="pt-3 mt-4">
            <v-radio
              v-for="(plan, i) of tppOptions" :key="`tP${i}`"
              data-testid="select-tpp-radio"
              class="select-tpp-radio tpp-option my-2"
              :label="`$${plan.coverage} plan - $${plan.premium}/month`"
              :value="plan"
            />
            <v-radio
              data-testid="decline-tpp-radio"
              class="decline-tpp-radio my-2"
              label="Decline protection plan"
              value=""
            />
            <v-radio
              data-testid="vehicle-storage-tpp-radio"
              class="vehicle-storage-tpp-radio my-2"
              label="Vehicle Storage (Decline)"
              value="vehicle"
            />
          </v-radio-group>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </stepper-layout>
</template>

<script>
/* eslint-disable no-return-assign */
import { mapGetters, mapState } from "vuex";
import { EventBus } from "@/Event";
import FacilityTenantSearch from "@/views/facilities/partials/FacilityTenantSearch";
import StepperLayout from "@/layouts/StepperLayout";
import moment from "moment";
import states from "@/helpers/states";
import idTypes from "@/helpers/tenant/tenant-id-types";

export default {
  props: {
    tpp: {
      type: Array,
      default: () => []
    },
    updateNeeded: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      moveInSelect: null,
      showAvailableUnits: false,
      states: states,
      idTypes: idTypes,
      idState: null,
      selectedOption: {
        name: null,
        id: 0
      },
      panel: 0
    };
  },

  methods: {
    deferPromotion(val) {
      // tell getMoveInCost to deferr promo to next month - needed for S360.
      this.$store.commit("setDeferPromotion", val);
    },
    handleResetForm() {
      this.$store.dispatch("handleResetTenant");
    },

    setTenant(selectedTenant) {
      this.$store.commit("updateInfo", Object.assign(selectedTenant, {
        phone_number: this.addDashes(selectedTenant.phone_number),
        is_business: !!(
          selectedTenant.business_name && selectedTenant.business_name.length
        ),
        waiting_id: ""
      }));
    },

    updateIDType() {
      this.$store.commit("updateIdType", this.selectedOption.id);
    },

    updateIDStateOld() {
      this.$store.commit("updateIdState", this.idState);
    },

    addDashes(phone) {
      if (phone) {
        if (phone.length === 10 && typeof phone.replace === "function") {
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        } else {
          return phone;
        }
      }
    },

    selectUnit(unit) {
      this.$store.commit("setUnit", unit);
      this.$emit("setUnit", unit);
    }
  },

  computed: {
    ...mapState({
      availableUnits: (state) => state.facilities.availableUnits,
      creditCard: (state) => state.tenant.creditCard,
      tenant: (state) => state.tenant.info,
      lease: (state) => state.tenant.lease,
      moveInCostLoading: (state) => state.facilities.moveInCostLoading,
      options: (state) => state.tenant.options,
      unit: (state) => state.facilities.unit,
      unitFeatures: (state) => state.facilities.unitFeatures,
      invalidPromotion: (state) => state.reservations.invalidPromotion,
      facilityPromotions: (state) => state.facilities.facilityPromotions
    }),

    ...mapGetters([
      "promotionOptionsLoading",
      "facilityPromotionsLoading",
      "defaultPromotionOption"
    ]),

    tppOptions() {
      return this.tpp
        .filter(plan => plan.coverage < 5001)
        .sort((a, b) => a.coverage - b.coverage);
    },

    isPassport() {
      return this.tenant.id_type === 3;
    },

    promotionOptions: {
      get() { return this.$store.state.facilities.promotionOptions; },
      set(value) { this.$store.commit("setPromotionOptions", value); }
    },

    defaultPromotionOptionId: {
      get() {
        if (!this.defaultPromotionOption) return null;
        if (
          !this.defaultPromotionOption.id &&
          this.defaultPromotionOption.id !== 0 &&
          this.defaultPromotionOption.id !== "0"
        ) {
          return null;
        }
        return this.defaultPromotionOption.id;
      },
      set(externalPromotionId) {
        this.selectedPromotion = externalPromotionId;
        this.$store.commit("setPromotionOptions", null);
        if (externalPromotionId === 0 || externalPromotionId === "0") {
          this.$store.commit("setPromotionOptions", {});
        } else {
          // if not null, false, zero, etc:
          this.$store.dispatch(
            "getPromotionOptions",
            {
              externalPromotionId: externalPromotionId,
              facilityId: this.facility.id
            });
        };
      }
    },

    userMustSelectANewPromotion() {
      if (!this.facilityPromotions) return false;
      if (!this.promotionOptions) return true;
      if (
        this.promotionOptions.this_month &&
        this.promotionOptions.this_month.id === -999
      ) {
        return true;
      }
      return false;
    },

    userCanChooseAnotherPromotion() {
      if (!this.invalidPromotion) {
        // currently we only allow changing a promotion if the reservation had
        // been associated with a promotion that was deleted from the PMS
        return false;
      }
      if (
        this.selectedPromotion ||
        this.selectedPromotion === 0
      ) {
        return true;
      }
      return false;
    },

    firstName: {
      get() {
        return this.tenant.first_name;
      },
      set(newVal) {
        this.$store.commit("updateFirstName", newVal);
      }
    },

    lastName: {
      get() {
        return this.tenant.last_name;
      },
      set(newVal) {
        this.$store.commit("updateLastName", newVal);
      }
    },

    address: {
      get() {
        return this.tenant.street_address;
      },
      set(newVal) {
        this.$store.commit("updateAddress", newVal);
      }
    },

    city: {
      get() {
        return this.tenant.city;
      },
      set(newVal) {
        this.$store.commit("updateCity", newVal);
      }
    },

    state: {
      get() {
        return this.tenant.state;
      },
      set(newVal) {
        this.$store.commit("updateState", newVal);
      }
    },

    postalCode: {
      get() {
        return this.tenant.postal_code;
      },
      set(newVal) {
        this.$store.commit("updatePostalCode", newVal);
      }
    },

    phone: {
      get() {
        return this.tenant.phone_number;
      },
      set(newVal) {
        this.$store.commit("updatePhone", newVal);
      }
    },

    email: {
      get() {
        return this.tenant.email;
      },
      set(newVal) {
        this.$store.commit("updateEmail", newVal);
      }
    },

    idNumber: {
      get() {
        return this.tenant.id_number;
      },
      set(newVal) {
        this.$store.commit("updateIdNumber", newVal);
      }
    },

    idTypeOption: {
      get() {
        if (!this.tenant.id_type) {
          return "";
        }
        let i = this.tenant.id_type - 1;
        if (i < 0) { // add this check for tenants that have not set id_type yet.
          i = 0;
        }
        let name = this.idTypes[i].name;
        this.selectedOption.id = this.tenant.id_type;
        this.selectedOption.name = name;
        return name;
      },
      set(newVal) {
        this.selectedOption.id = newVal.id;
        let i = newVal.id - 1;
        let name = this.idTypes[i].name;
        this.selectedOption.name = name;
        this.$store.commit("updateIdType", newVal.id);
        if (i === 2) { // if passport, then clear the state
          this.$store.commit("updateIdState", null);
        }
      }
    },

    idStateOption: {
      get() {
        return (this.tenant.id_state ? this.tenant.id_state : "");
      },
      set(newVal) {
        let k = newVal.value;
        this.$store.commit("updateIdState", k);
      }
    },

    isBusinessCustomer: {
      get() {
        return this.tenant.is_business;
      },
      set(newVal) {
        this.$store.commit("updateIsBusinessCustomer", newVal);
      }
    },

    companyName: {
      get() {
        return this.tenant.business_name ? this.tenant.business_name : "";
      },
      set(newVal) {
        this.$store.commit("updateCompanyName", newVal);
      }
    },

    moveInDate: {
      get() {
        return this.options.moveInDate;
      },
      set(newVal) {
        this.$store.commit("updateMoveInDate", newVal);
      }
    },

    selectedPromotion: {
      get() {
        if (
          this.options.selectedPromotion ||
          this.options.selectedPromotion === 0
        ) {
          return this.options.selectedPromotion;
        } else if (
          this.defaultPromotionOptionId ||
          this.defaultPromotionOptionId === 0
        ) {
          return this.defaultPromotionOptionId;
        } else {
          return null;
        }
      },
      set(newVal) {
        this.$store.commit("updatePromotion", newVal);
      }
    },

    tenantProtection: {
      get() {
        return this.options.tenantProtection;
      },
      set(newVal) {
        this.$store.commit("updateTenantProtection", newVal);
      }
    },

    facility() {
      return this.$store.state.facilities.facility;
    },

    today() {
      let now = new Date();
      return new moment(now).format("YYYY-MM-DD");
    },

    moveInMax() {
      let date = new Date();
      return new moment(date).add(21, "days").format("YYYY-MM-DD");
    },

    unitDimensions() {
      if (this.unitFeatures) {
        if (this.unitFeatures.width) {
          return (
            "'" + this.unitFeatures.width + " x '" + this.unitFeatures.length
          );
        } else {
          return "'" + this.unitFeatures.length + " Parking";
        }
      } else {
        return "";
      }
    }
  },
  created() {
    EventBus.$on("resetPanel", () => (this.panel = 0));
  },
  components: {
    StepperLayout,
    FacilityTenantSearch
  }
};
</script>
